import { Bell, Calendar, House, Menu, User, UsersRound } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { paths } from '@/app/paths';
import { cn } from '@/design-system/lib/cn';
import { useNotifications } from '@/hooks/useNotifications';
import { useStore } from '@/store/AppStore';

import { isActive } from './navigation';

/**
 * Navegação do celular (< 760px), fixa no rodapé com a área segura. Admin e líderes têm
 * "Mais", que abre a gaveta com o restante; o Membro tem os avisos à mão.
 */
export default function BottomNav({ onMore }: { onMore: () => void }) {
  const { pathname } = useLocation();
  const { permissions } = useStore();
  const { unread } = useNotifications();

  const items = permissions.isManager
    ? [
        { to: paths.home, label: 'Início', icon: House },
        { to: paths.schedules, label: 'Calendário', icon: Calendar },
        { to: paths.membership, label: 'Membresia', icon: UsersRound },
      ]
    : [
        { to: paths.home, label: 'Início', icon: House },
        { to: paths.schedules, label: 'Calendário', icon: Calendar },
        { to: paths.notifications, label: 'Avisos', icon: Bell, badge: unread },
        { to: paths.me, label: 'Perfil', icon: User },
      ];

  const base =
    'relative flex min-h-13 flex-col items-center justify-center gap-0.75 rounded-control text-caption no-underline [&_svg]:size-5 [&_svg]:stroke-[1.8]';

  return (
    <nav
      aria-label="Navegação"
      data-bottom-nav
      className="fixed inset-x-0 bottom-0 z-45 grid auto-cols-fr grid-flow-col border-t border-line-input bg-white px-1 pt-1.5 pb-[calc(6px+env(safe-area-inset-bottom))] shadow-bottom-nav md:hidden"
    >
      {items.map((item) => {
        const active = isActive(item, pathname);
        const Icon = item.icon;

        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            aria-label={'badge' in item && item.badge ? `${item.label}: ${item.badge} não lidas` : item.label}
            className={cn(base, active ? 'font-semibold text-primary' : 'font-medium text-ink-subtle')}
          >
            <Icon aria-hidden />
            <span aria-hidden>{item.label}</span>
            {'badge' in item && item.badge ? (
              <span
                aria-hidden
                className="absolute top-1 left-[calc(50%+6px)] grid h-4 min-w-4 place-items-center rounded-pill bg-status-conflict-bar px-1 text-micro font-bold text-white"
              >
                {item.badge}
              </span>
            ) : null}
          </Link>
        );
      })}
      {permissions.isManager ? (
        <button type="button" onClick={onMore} className={cn(base, 'font-medium text-ink-subtle')}>
          <Menu aria-hidden />
          Mais
        </button>
      ) : null}
    </nav>
  );
}
